import { deleteScheduledPaymentGroup, retryScheduledPaymentGroup } from '@/services/batchPayment';
import {
  deleteScheduledPayment,
  getScheduledPayments,
  retryScheduledPayment
} from '@/services/scheduledPayment';
import {
  deactivateScheduledWalletTransfer,
  retryScheduledWalletTransfer
} from '@/services/scheduledWalletTransfer';
import { BatchDeletionErrorCodeEnum } from '@/features/payments/types/BatchPayment';
import {
  PaymentRowKindEnum,
  ScheduledPaymentDeletionErrorCodeEnum,
  TPaymentListItem
} from '../types/ManagePayments';
import { TPaymentActionStrategy } from '../types/PaymentKindStrategy';

/**
 * The actual per-kind `TPaymentActionStrategy` implementations - deliberately kept in their own
 * file, separate from data/paymentActionStrategies.ts's registry/lookup ("process"), so adding a
 * new kind's behaviour never means editing that process file's own logic, only adding a new
 * export here and one registration line there.
 */

// Shared by both non-batch strategies below - deactivate()/DELETE-payment never returns anything
// equivalent to a batch's DELETION_NOT_ALLOWED/DELETION_CONFLICT (see
// ScheduledPaymentDeletionErrorCodeEnum's own doc comment), so NOT_FOUND is the only code either
// one's delete endpoint can mean "already gone" with.
const isScheduledPaymentDeletionNotFound = (code: string | undefined): boolean =>
  code === ScheduledPaymentDeletionErrorCodeEnum.NOT_FOUND;

/**
 * Resolves the real EScheduledBankPayment id to call DELETE/POST retry /scheduled-payments/{id}
 * with. For a `scheduled_payment` row, `payment.id` already IS that id - sourced straight from
 * getScheduledPayments. For a `pending_scheduled_payment` row, `payment.id` is the multi-sign
 * action's id, and stays that way even once approve()/the final approval's synchronous execution
 * patches its `state` to APPROVED or FAILED in place (see useScheduledPaymentAction's approve())
 * - authoriseMultiSignAction's response never returns the payment the action's completion just
 * created (the backend use case creates it purely for notifications, then discards it), so
 * there's no real id to patch in with at that point, for either the delete or the retry path.
 *
 * This does a fresh lookup instead, matched by content - the same walletId, payee bank account,
 * amount and scheduled date the payment was created with, since nothing else ties the multi-sign
 * action to the payment it produced. Returns null if the account's payments can't be fetched, or
 * no confident match is found (e.g. it was already deleted/paid elsewhere in the meantime), so
 * the caller falls back to its own generic error banner rather than risk deleting/retrying the
 * wrong payment. A hard page refresh sidesteps this entirely, since a freshly-fetched row is
 * already `kind: scheduled_payment` with its real id - this only matters same-session, right
 * after approving. Private to scheduledPaymentActionStrategy below - the only strategy that needs
 * it (a scheduled transfer never carries a multiSignActionId, so it has no equivalent kind).
 *
 * Passes both ids straight through to getScheduledPayments, which itself prioritises businessId
 * over accountId when both are present (accountId only applies to a personal account).
 */
const resolveScheduledPaymentId = async (
  payment: TPaymentListItem,
  accountId: string | undefined,
  businessId: string | undefined
): Promise<string | null> => {
  if (payment.kind !== PaymentRowKindEnum.PENDING_SCHEDULED_PAYMENT) return payment.id;
  if (!accountId && !businessId) return null;

  let candidates: TPaymentListItem[];
  try {
    candidates = await getScheduledPayments(accountId, businessId);
  } catch {
    return null;
  }

  const payeeBankAccount = payment.paymentDetail?.payeeBankAccount;
  if (!payeeBankAccount) return null;

  const matches = candidates.filter(
    (candidate) =>
      candidate.walletId === payment.walletId &&
      candidate.totalAmount === payment.totalAmount &&
      candidate.scheduledDate === payment.scheduledDate &&
      candidate.paymentDetail?.payeeBankAccount === payeeBankAccount
  );

  return matches.length === 1 ? matches[0].id : null;
};

export const batchActionStrategy: TPaymentActionStrategy = {
  deletePayment: async (payment) => {
    await deleteScheduledPaymentGroup(payment.id);
  },
  isDeleteAlreadyGoneError: (code) => code === BatchDeletionErrorCodeEnum.NOT_FOUND,
  isDeleteConflictError: (code) =>
    code === BatchDeletionErrorCodeEnum.DELETION_NOT_ALLOWED ||
    code === BatchDeletionErrorCodeEnum.DELETION_CONFLICT,
  retryPayment: async (payment) => {
    const response = await retryScheduledPaymentGroup(payment.id);
    return {
      id: payment.id,
      state: response.data.state,
      forceCompleted: response.data.forceCompleted
    };
  }
};

// Covers scheduled_payment and pending_scheduled_payment alike (see the registry in
// paymentActionStrategies.ts) - the only difference between the two is
// resolveScheduledPaymentId's own id-resolution, which already branches on `payment.kind`
// internally.
export const scheduledPaymentActionStrategy: TPaymentActionStrategy = {
  deletePayment: async (payment, ctx) => {
    const paymentId = await resolveScheduledPaymentId(payment, ctx.accountId, ctx.businessId);
    if (!paymentId) {
      // No confident match found (or the lookup itself failed) - throw so the generic
      // confirmDelete catch block falls back to the same delete-error banner a network failure
      // would, rather than silently succeeding.
      throw new Error('Unable to resolve the scheduled payment id to delete');
    }
    await deleteScheduledPayment(paymentId);
  },
  isDeleteAlreadyGoneError: isScheduledPaymentDeletionNotFound,
  // No isDeleteConflictError - an individual scheduled payment's delete
  // (DeactivateScheduledBankPaymentUseCaseImpl) has no state/processing-window check at all, so it
  // never returns anything equivalent to a batch's DELETION_NOT_ALLOWED/DELETION_CONFLICT.
  retryPayment: async (payment, ctx) => {
    const paymentId = await resolveScheduledPaymentId(payment, ctx.accountId, ctx.businessId);
    if (!paymentId) {
      // Same reasoning as deletePayment's throw above - falls back to retry()'s generic
      // RETRY_ERROR_BANNER_BY_KIND banner rather than risk retrying the wrong payment (or posting
      // to /scheduled-payments/{multiSignActionId}/retry, which 404s/acts on the wrong resource).
      throw new Error('Unable to resolve the scheduled payment id to retry');
    }
    // No response body to patch the row with - it's removed/patched by the caller instead (see
    // TPaymentActionStrategy's own doc comment on retryPayment).
    await retryScheduledPayment(paymentId);
    return undefined;
  }
};

export const scheduledTransferActionStrategy: TPaymentActionStrategy = {
  deletePayment: async (payment) => {
    await deactivateScheduledWalletTransfer(payment.id);
  },
  isDeleteAlreadyGoneError: isScheduledPaymentDeletionNotFound,
  retryPayment: async (payment) => {
    await retryScheduledWalletTransfer(payment.id);
    return undefined;
  }
};
